import { z } from 'zod/v4';
import {
  chatRoomTypeEnum as chatRoomTypeSchema,
  chatParticipantRoleEnum as chatParticipantRoleSchema,
  chatParticipantStatusEnum as chatParticipantStatusSchema,
  messageDeliveryStatusEnum as messageDeliveryStatusSchema,
  type ChatRoomType,
  type ChatParticipantRole,
  type ChatParticipantStatus,
  type MessageDeliveryStatus,
} from '../enums/index';

// ==========================================
// DTOs - Create
// ==========================================

export const createDirectChatSchema = z.object({
  targetUserId: z.number({ message: 'ID пользователя обязателен' })
    .int('ID пользователя должен быть целым числом')
    .positive('ID пользователя должен быть положительным'),
});
export type CreateDirectChatDto = z.infer<typeof createDirectChatSchema>;

export const createSupportChatSchema = z.object({
  subject: z.string()
    .min(1, 'Тема не может быть пустой')
    .max(200, 'Тема слишком длинная (максимум 200 символов)')
    .optional(),
});
export type CreateSupportChatDto = z.infer<typeof createSupportChatSchema>;

export const createGameLobbyChatSchema = z.object({
  lobbyId: z.number({ message: 'ID лобби обязателен' })
    .int('ID лобби должен быть целым числом')
    .positive('ID лобби должен быть положительным'),
  name: z.string()
    .min(1, 'Название не может быть пустым')
    .max(100, 'Название слишком длинное (максимум 100 символов)')
    .optional(),
});
export type CreateGameLobbyChatDto = z.infer<typeof createGameLobbyChatSchema>;

// E2EE — Megolm payload для одного сообщения.
export const encryptedContentPayloadSchema = z.object({
  ciphertext: z.string().min(1),
  sessionId: z.string().min(1),
  messageIndex: z.number().int().nonnegative(),
  senderDeviceId: z.number().int().positive(),
});
export type EncryptedContentPayloadDto = z.infer<typeof encryptedContentPayloadSchema>;

// Per-device копия payload-а (для multi-device sync).
export const perDeviceBlobPayloadSchema = z.object({
  targetDeviceId: z.number().int().positive(),
  ciphertext: z.string().min(1),
});
export type PerDeviceBlobPayloadDto = z.infer<typeof perDeviceBlobPayloadSchema>;

// Зашифрованный файл в E2EE-чате.
export const encryptedMediaPayloadSchema = z.object({
  blobUrl: z.string().min(1),
  mimeType: z.string().min(1),
  sizeBytes: z.number().int().nonnegative(),
  keyRefInMessage: z.string().min(1),
});
export type EncryptedMediaPayloadDto = z.infer<typeof encryptedMediaPayloadSchema>;

export const sendMessageSchema = z.object({
  // Plaintext path (не-E2EE комнаты).
  content: z.string({ message: 'Сообщение обязательно' })
    .max(4000, 'Сообщение слишком длинное (максимум 4000 символов)')
    .optional(),
  replyToMessageId: z.number()
    .int('ID сообщения должен быть целым числом')
    .positive('ID сообщения должен быть положительным')
    .optional(),
  attachmentFileIds: z.array(
    z.number().int('ID файла должен быть целым числом').positive('ID файла должен быть положительным')
  ).max(5, 'Максимум 5 вложений').optional(),
  // E2EE path — Megolm ciphertext + per-device история + шифрованные медиа.
  // Когда комната isE2EE, бэкенд проверит что именно эти поля заполнены, а
  // plaintext content/attachments — запрещены (и наоборот для non-E2EE).
  encryptedContent: encryptedContentPayloadSchema.optional(),
  perDeviceBlobs: z.array(perDeviceBlobPayloadSchema).optional(),
  encryptedMedia: z.array(encryptedMediaPayloadSchema).optional(),
}).refine(
  (data) =>
    (data.content !== undefined && data.content.trim().length > 0) ||
    (data.attachmentFileIds !== undefined && data.attachmentFileIds.length > 0) ||
    data.encryptedContent !== undefined,
  'Сообщение должно содержать текст, вложения или encryptedContent'
);
export type SendMessageDto = z.infer<typeof sendMessageSchema>;

export const editMessageSchema = z.object({
  content: z.string({ message: 'Сообщение обязательно' })
    .min(1, 'Сообщение не может быть пустым')
    .max(4000, 'Сообщение слишком длинное (максимум 4000 символов)'),
});
export type EditMessageDto = z.infer<typeof editMessageSchema>;

export const addReactionSchema = z.object({
  emoji: z.string({ message: 'Эмодзи обязателен' })
    .min(1, 'Эмодзи обязателен')
    .max(10, 'Эмодзи слишком длинный'),
});
export type AddReactionDto = z.infer<typeof addReactionSchema>;

export const markReadSchema = z.object({
  messageId: z.number({ message: 'ID сообщения обязателен' })
    .int('ID сообщения должен быть целым числом')
    .positive('ID сообщения должен быть положительным'),
});
export type MarkReadDto = z.infer<typeof markReadSchema>;

// ==========================================
// DTOs - Filters
// ==========================================

export const chatRoomsFilterSchema = z.object({
  type: chatRoomTypeSchema.optional(),
  page: z.coerce.number().int('Страница должна быть целым числом').positive('Страница должна быть положительной').default(1),
  limit: z.coerce.number().int('Лимит должен быть целым числом').min(1, 'Лимит должен быть >= 1').max(100, 'Лимит должен быть <= 100').default(20),
});
export type ChatRoomsFilterDto = z.infer<typeof chatRoomsFilterSchema>;

export const messagesFilterSchema = z.object({
  cursor: z.coerce.number().int('Курсор должен быть целым числом').positive('Курсор должен быть положительным').optional(),
  limit: z.coerce.number().int('Лимит должен быть целым числом').min(1, 'Лимит должен быть >= 1').max(100, 'Лимит должен быть <= 100').default(50),
  direction: z.enum(['before', 'after']).default('before'),
  search: z.string().min(1, 'Поисковый запрос не может быть пустым').max(200, 'Поисковый запрос слишком длинный').optional(),
});
export type MessagesFilterDto = z.infer<typeof messagesFilterSchema>;

export const searchMessagesFilterSchema = z.object({
  query: z.string({ message: 'Поисковый запрос обязателен' })
    .min(1, 'Поисковый запрос не может быть пустым')
    .max(200, 'Поисковый запрос слишком длинный'),
  limit: z.coerce.number().int('Лимит должен быть целым числом').min(1, 'Лимит должен быть >= 1').max(100, 'Лимит должен быть <= 100').default(50),
  roomId: z.coerce.number().int('ID комнаты должен быть целым числом').positive('ID комнаты должен быть положительным').optional(), // опционально ограничить поиск одной комнатой
});
export type SearchMessagesFilterDto = z.infer<typeof searchMessagesFilterSchema>;

export interface SearchMessageResult {
  id: number;
  chatRoomId: number;
  content: string;
  createdAt: Date;
  sender: {
    type: 'user' | 'admin';
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    username?: string | null;
    photoUrl?: string | null;
  } | null;
  room: {
    id: number;
    type: ChatRoomType;
    name: string | null;
  };
  // Контекст для подсветки результата поиска
  matchHighlight?: string;
}

// ==========================================
// Response Types
// ==========================================

export interface ChatRoomResponse {
  id: number;
  type: ChatRoomType;
  // Семантический kind; backend возвращает его из schema chatRoom.kind.
  // Optional для совместимости со старыми клиентами, которые ожидают только `type`.
  kind?: string;
  // E2EE-флаг комнаты. Клиент по этому флагу переключает composer в
  // encrypt-on-send и не рендерит plaintext content в bubble.
  isE2EE?: boolean;
  name: string | null;
  isActive: boolean;
  lastMessageAt: Date | null;
  createdAt: Date;
  unreadCount: number;
  participants: ChatParticipantResponse[];
  lastMessage?: ChatMessageResponse | null;
}

export interface ChatParticipantResponse {
  id: number;
  chatRoomId: number;
  userId: number | null;
  adminUserId: number | null;
  role: ChatParticipantRole;
  status: ChatParticipantStatus;
  isMuted: boolean;
  lastReadAt: Date | null;
  joinedAt: Date;
  departmentName?: string | null;
  user?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    photoUrl: string | null;
    hasActiveSubscription?: boolean;
  } | null;
  adminUser?: {
    id: number;
    username: string;
    email: string;
  } | null;
}

export interface ChatMessageAttachmentResponse {
  id: number;
  fileId: number;
  type: 'image' | 'video' | 'audio' | 'document';
  url: string;
  name: string;
  size: number;
  mimeType: string;
}

export interface ChatMessageResponse {
  id: number;
  chatRoomId: number;
  content: string;
  isSystemMessage: boolean;
  replyToMessageId: number | null;
  status: MessageDeliveryStatus;
  isEdited: boolean;
  editedAt: Date | null;
  isDeleted: boolean;
  createdAt: Date;
  sender: {
    type: 'user' | 'admin';
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    username?: string | null;
    photoUrl?: string | null;
    departmentName?: string | null;
    hasActiveSubscription?: boolean;
  } | null;
  replyTo?: ChatMessageResponse | null;
  reactions: ReactionResponse[];
  readBy: ReadReceiptResponse[];
  attachments: ChatMessageAttachmentResponse[];
  // E2EE — присутствует только для сообщений в E2EE-комнатах. В plaintext
  // `content` в этом случае пустая строка; реальный payload — в
  // encryptedContent.ciphertext. `blobForDevice` — per-device копия payload
  // под текущее устройство (deviceRowId из X-Device-Id / WS ?deviceId).
  encryptedContent?: {
    ciphertext: string;
    sessionId: string;
    messageIndex: number;
    senderDeviceId: number;
  } | null;
  blobForDevice?: { ciphertext: string } | null;
  encryptedMedia?: Array<{
    id: number;
    blobUrl: string;
    mimeType: string;
    sizeBytes: number;
    keyRefInMessage: string;
  }>;
}

export interface ReactionResponse {
  emoji: string;
  count: number;
  users: Array<{
    type: 'user' | 'admin';
    id: number;
    firstName?: string | null;
    username?: string | null;
  }>;
  hasReacted: boolean;
}

export interface ReadReceiptResponse {
  type: 'user' | 'admin';
  id: number;
  firstName?: string | null;
  username?: string | null;
  readAt: Date;
}

// ==========================================
// WebSocket Event Types
// ==========================================

// Client -> Server
export interface WsSubscribeRoom {
  type: 'subscribe_room';
  roomId: number;
}

export interface WsUnsubscribeRoom {
  type: 'unsubscribe_room';
  roomId: number;
}

export interface WsSendMessage {
  type: 'send_message';
  roomId: number;
  // Plaintext (не-E2EE комнаты). Optional: для E2EE-комнат вместо content
  // клиент шлёт encryptedContent. Backend валидирует consistency с room.isE2EE.
  content?: string;
  replyToMessageId?: number;
  attachmentFileIds?: number[];
  // E2EE path — зеркалит HTTP `POST /chat/:roomId/messages` body.
  encryptedContent?: EncryptedContentPayloadDto;
  perDeviceBlobs?: PerDeviceBlobPayloadDto[];
  encryptedMedia?: EncryptedMediaPayloadDto[];
}

// Runtime-валидация frame'а `send_message` из WebSocket. Использует тот же
// refine, что HTTP sendMessageSchema, поэтому правила "или plaintext, или
// encryptedContent" одинаковы на обоих транспортах.
export const wsSendMessageSchema = z.object({
  type: z.literal('send_message'),
  roomId: z.number().int().positive(),
  content: z.string().max(4000).optional(),
  replyToMessageId: z.number().int().positive().optional(),
  attachmentFileIds: z.array(z.number().int().positive()).max(5).optional(),
  encryptedContent: encryptedContentPayloadSchema.optional(),
  perDeviceBlobs: z.array(perDeviceBlobPayloadSchema).optional(),
  encryptedMedia: z.array(encryptedMediaPayloadSchema).optional(),
}).refine(
  (data) =>
    (data.content !== undefined && data.content.trim().length > 0) ||
    (data.attachmentFileIds !== undefined && data.attachmentFileIds.length > 0) ||
    data.encryptedContent !== undefined,
  'Сообщение должно содержать текст, вложения или encryptedContent',
);
export type WsSendMessagePayload = z.infer<typeof wsSendMessageSchema>;

export interface WsEditMessage {
  type: 'edit_message';
  messageId: number;
  content: string;
}

export interface WsDeleteMessage {
  type: 'delete_message';
  messageId: number;
}

export interface WsAddReaction {
  type: 'add_reaction';
  messageId: number;
  emoji: string;
}

export interface WsRemoveReaction {
  type: 'remove_reaction';
  messageId: number;
  emoji: string;
}

export interface WsMarkRead {
  type: 'mark_read';
  roomId: number;
  messageId: number;
}

export interface WsTypingStart {
  type: 'typing_start';
  roomId: number;
}

export interface WsTypingStop {
  type: 'typing_stop';
  roomId: number;
}

export interface WsPong {
  type: 'pong';
}

export interface WsClientPing {
  type: 'ping';
}

/**
 * Matrix CSAPI to-device delivery ACK. Client confirms receipt of queued
 * `matrix.to_device` events so the server can mark them delivered and
 * stop re-flushing them on reconnect.
 */
export interface WsMatrixToDeviceAck {
  type: 'matrix.to_device.ack';
  eventIds: number[];
}

export type WsClientMessage =
  | WsSubscribeRoom
  | WsUnsubscribeRoom
  | WsSendMessage
  | WsEditMessage
  | WsDeleteMessage
  | WsAddReaction
  | WsRemoveReaction
  | WsMarkRead
  | WsTypingStart
  | WsTypingStop
  | WsPong
  | WsClientPing
  | WsMatrixToDeviceAck;

// Server -> Client
export interface WsConnected {
  type: 'connected';
  userId: number;
  isAdmin: boolean;
}

export interface WsSubscribed {
  type: 'subscribed';
  roomId: number;
  pinnedMessage?: ChatMessageResponse | null;
  pinnedMessages?: ChatMessageResponse[];
}

export interface WsNewMessage {
  type: 'new_message';
  message: ChatMessageResponse;
  // ChatRoomKind отправляющей комнаты. Фронт исключает LIVESTREAM
  // из общего счётчика непрочитанных, чтобы поток сообщений эфира
  // не раздувал badge /chat.
  chatRoomKind?: string;
}

export interface WsMessageEdited {
  type: 'message_edited';
  messageId: number;
  roomId: number;
  content: string;
  editedAt: Date;
}

export interface WsMessageDeleted {
  type: 'message_deleted';
  messageId: number;
  roomId: number;
}

export interface WsReactionAdded {
  type: 'reaction_added';
  messageId: number;
  roomId: number;
  emoji: string;
  user: {
    type: 'user' | 'admin';
    id: number;
    firstName?: string | null;
    username?: string | null;
  };
}

export interface WsReactionRemoved {
  type: 'reaction_removed';
  messageId: number;
  roomId: number;
  emoji: string;
  userId: number;
  userType: 'user' | 'admin';
}

export interface WsMessageRead {
  type: 'message_read';
  messageId: number;
  roomId: number;
  reader: {
    type: 'user' | 'admin';
    id: number;
    firstName?: string | null;
    username?: string | null;
  };
  readAt: Date;
}

export interface WsUserTyping {
  type: 'user_typing';
  roomId: number;
  user: {
    type: 'user' | 'admin';
    id: number;
    firstName?: string | null;
    username?: string | null;
  };
}

export interface WsUserStoppedTyping {
  type: 'user_stopped_typing';
  roomId: number;
  userId: number;
  userType: 'user' | 'admin';
}

export interface WsUserOnline {
  type: 'user_online';
  user: {
    type: 'user' | 'admin';
    id: number;
  };
}

export interface WsUserOffline {
  type: 'user_offline';
  userId: number;
  userType: 'user' | 'admin';
  lastSeenAt: Date;
}

export interface WsPing {
  type: 'ping';
}

export interface WsError {
  type: 'error';
  message: string;
  code?: string;
}

export interface WsUserKicked {
  type: 'user_kicked';
  userId: number;
  roomId: number;
}

export interface WsUserMuted {
  type: 'user_muted';
  userId: number;
  roomId: number;
  mutedUntil: string | null;
}

export interface WsUserWarned {
  type: 'user_warned';
  userId: number;
  roomId: number;
  reason: string;
}

export interface WsUserUnmuted {
  type: 'user_unmuted';
  userId: number;
  roomId: number;
}

export interface WsUserUnbanned {
  type: 'user_unbanned';
  userId: number;
  roomId: number;
}

export interface WsRoomUpdate {
  type: 'room_update';
  roomId: number;
  lastMessage: {
    id: number;
    content: string;
    createdAt: string;
    sender: {
      type: 'user' | 'admin';
      id: number;
      firstName: string | null;
      lastName: string | null;
      username: string | null;
    } | null;
  };
  lastMessageAt: string;
}

export interface WsRoomClosed {
  type: 'room_closed';
  roomId: number;
}

export interface WsMessagePinned {
  type: 'message_pinned';
  roomId: number;
  message: ChatMessageResponse;
  pinnedMessages?: ChatMessageResponse[];
}

export interface WsMessageUnpinned {
  type: 'message_unpinned';
  roomId: number;
  messageId?: number;
  pinnedMessages?: ChatMessageResponse[];
}

/**
 * Matrix CSAPI to-device payload pushed to the recipient device.
 * `content` is opaque ciphertext (Olm/Megolm payload). Client ACKs
 * receipt via `matrix.to_device.ack` with the included `id`s.
 */
export interface WsMatrixToDevice {
  type: 'matrix.to_device';
  events: Array<{
    id: number;
    sender: string;
    type: string;
    content: unknown;
  }>;
}

export interface WsCallParticipantJoined {
  type: 'call_participant_joined';
  roomId: string;
  chatRoomId: number;
  userId: number;
}

export interface WsCallParticipantLeft {
  type: 'call_participant_left';
  roomId: string;
  chatRoomId: number;
  userId: number;
}

export interface WsCallEnded {
  type: 'call_ended';
  roomId: string;
  chatRoomId: number;
}

export interface WsCallIncoming {
  type: 'call_incoming';
  roomId: string;
  roomType: 'CALL' | 'STAGE';
  callerId: number;
  callerName: string | null;
}

export interface WsCallInviteToStage {
  type: 'call_invite_stage';
  invitationId: string;
  roomId: string;
  inviterName: string | null;
}

// Сессионные залы (breakout rooms) трансляции
export interface WsBreakoutOpened {
  type: 'breakout_opened';
  livestreamId: number;
  sessionId: string;
}

export interface WsBreakoutClosed {
  type: 'breakout_closed';
  livestreamId: number;
}

export interface WsBreakoutReassigned {
  type: 'breakout_reassigned';
  livestreamId: number;
}

export type WsServerMessage =
  | WsConnected
  | WsSubscribed
  | WsNewMessage
  | WsMessageEdited
  | WsMessageDeleted
  | WsReactionAdded
  | WsReactionRemoved
  | WsMessageRead
  | WsUserTyping
  | WsUserStoppedTyping
  | WsUserOnline
  | WsUserOffline
  | WsPing
  | WsError
  | WsUserKicked
  | WsUserMuted
  | WsUserWarned
  | WsUserUnmuted
  | WsUserUnbanned
  | WsRoomUpdate
  | WsRoomClosed
  | WsMatrixToDevice
  | WsMessagePinned
  | WsMessageUnpinned
  | WsCallIncoming
  | WsCallInviteToStage
  | WsCallParticipantJoined
  | WsCallParticipantLeft
  | WsCallEnded
  | WsBreakoutOpened
  | WsBreakoutClosed
  | WsBreakoutReassigned;

// Re-export enum schemas and types for compatibility
export {
  chatRoomTypeSchema,
  chatParticipantRoleSchema,
  chatParticipantStatusSchema,
  messageDeliveryStatusSchema,
};

export type {
  ChatRoomType,
  ChatParticipantRole,
  ChatParticipantStatus,
  MessageDeliveryStatus,
};
